"use client";

import './input-field.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useRef, useId, useState, useEffect } from 'react';
import Error from '../../icons/component-icons/error/index.js';
import CheckmarkAlt from '../../icons/component-icons/checkmark-alt/index.js';
import { validateSurface } from '../../globals.js';
import styles from './input-field.module.scss.js';
import getOS from '../../utilities/check-os/index.js';
import checkIfMobileDevice from '../../utilities/check-device/index.js';
import getBrowser from '../../utilities/check-browser/index.js';
import calculateRem from '../../utilities/calculate-rem/index.js';
import ScreenReaderText from '../../utilities/screen-reader-text/index.js';
import '../../utilities/use-viewport/index.js';
import { HelperText } from '../../form-elements/helper-text/index.js';
import { StatusText } from '../../form-elements/status-text/index.js';
import { Label } from '../../form-elements/label/index.js';
import SecurityCodeAmex from '../../icons/component-icons/security-code-amex/index.js';
import SecurityCode from '../../icons/component-icons/security-code/index.js';
import Visa from '../../icons/component-icons/visa/index.js';
import VisaInverted from '../../icons/component-icons/visa-inverted/index.js';
import Amex from '../../icons/component-icons/amex/index.js';
import Dinersclub from '../../icons/component-icons/dinersclub/index.js';
import Discover from '../../icons/component-icons/discover/index.js';
import Jcb from '../../icons/component-icons/jcb/index.js';
import Mastercard from '../../icons/component-icons/mastercard/index.js';
import UnionPay from '../../icons/component-icons/union-pay/index.js';
import PlaceholderInverted from '../../icons/component-icons/placeholder-inverted/index.js';
import Placeholder from '../../icons/component-icons/placeholder/index.js';
import { _calculateMaxLength, stickyPlaceholderHTML, _maskValue, _onlyNumbers, _ignoreAlpha, dateOnChange, _formatToPhoneDash, creditCardOnChange } from './input-formatting.js';
import '../../buttons/button/index.js';
import { TextLink } from '../../buttons/text-link/index.js';
import '../../icons/component-icons/left-caret-bold/index.js';
import '../../icons/component-icons/right-caret-bold/index.js';
import '../../buttons/button-icon/index.js';

const InputField = (_a) => {
    var _b, _c;
    var { helperTextPosition = 'bottom', containerBackground = 'transparent', label = undefined, maskBehavior = 'always', dateFormat = 'mmddyyyy', surface: surfaceProp = 'lightPrimary', 'data-testid': testId = 'input-field', autoComplete = 'off', isOpen = false, containsPii = false, required = true, inputId, id, width, type, disabled, tooltip, cardType: cardBrand, helperText, renderInputIcon, readOnly, error: errorProp, success, onClick, errorText: errorTextProp, ariaLabel, value: valueProp, caretColor, maxLength, defaultValue, onPaste, onChange, onFocus, onBlur, showPasswordButton = { label: 'Show' }, hidePasswordButton = { label: 'Hide' }, inlineActionButton, successText, inputRef } = _a, rest = __rest(_a, ["helperTextPosition", "containerBackground", "label", "maskBehavior", "dateFormat", "surface", 'data-testid', "autoComplete", "isOpen", "containsPii", "required", "inputId", "id", "width", "type", "disabled", "tooltip", "cardType", "helperText", "renderInputIcon", "readOnly", "error", "success", "onClick", "errorText", "ariaLabel", "value", "caretColor", "maxLength", "defaultValue", "onPaste", "onChange", "onFocus", "onBlur", "showPasswordButton", "hidePasswordButton", "inlineActionButton", "successText", "inputRef"]);
    const fieldId = useRef(null);
    const _useId = useId();
    if (!fieldId.current) {
        fieldId.current = inputId || _useId;
    }
    const componentContainerId = id || `${fieldId.current}-container`;
    const labelId = `${fieldId.current}-label`;
    const isMobileDevice = checkIfMobileDevice();
    const isAndroidDevice = getOS() === 'android';
    const isCalendar = type === 'calendar';
    const isSuccessSupported = ['text', 'tel', 'password'].includes(type);
    const [error, setError] = useState(false);
    const [emptyError, setEmptyError] = useState(false);
    const [cardType, setCardType] = useState(undefined);
    const timeoutRef = useRef(null);
    const _setInitialValue = () => {
        if (valueProp)
            return valueProp;
        if (defaultValue)
            return defaultValue;
        return '';
    };
    const [value, setValue] = useState(_setInitialValue());
    const [masked, setMasked] = useState(type === 'password');
    const [keyboardFocused, setKeyboardFocused] = useState(false);
    const [focusedInput, setFocusedInput] = useState(false);
    const [showCardTypeAlert, setShowCardTypeAlert] = useState(false);
    const inlineButtonWrapperRef = useRef(null);
    const alertTimeoutRef = useRef(null);
    const _listenForTab = (e) => {
        if (e.key === 'Tab')
            setKeyboardFocused(true);
    };
    useEffect(() => {
        if (valueProp && value !== valueProp)
            setValue(valueProp);
    }, [valueProp]);
    useEffect(() => {
        if (!required)
            setError(false);
    }, [required]);
    useEffect(() => {
        if (error !== errorProp) {
            setError(typeof errorProp === 'boolean' ? errorProp : false);
        }
    }, [errorProp]);
    useEffect(() => {
        document.addEventListener('keydown', _listenForTab);
        return () => document.removeEventListener('keydown', _listenForTab);
    }, []);
    useEffect(() => {
        if (cardType) {
            if (alertTimeoutRef.current) {
                clearTimeout(alertTimeoutRef.current);
            }
            setShowCardTypeAlert(true);
            alertTimeoutRef.current = setTimeout(() => {
                setShowCardTypeAlert(false);
            }, 500);
        }
        return () => {
            if (alertTimeoutRef.current) {
                clearTimeout(alertTimeoutRef.current);
            }
        };
    }, [cardType]);
    let errorText = '';
    if (error && errorTextProp) {
        errorText = errorTextProp;
    }
    else if (emptyError) {
        errorText = `You must enter a ${label}`;
    }
    else if (error) {
        errorText = `Your entry isn't valid. Please try again.`;
    }
    const showPassword = value && value.length > 0 && maskBehavior === 'toggle';
    const renderInlineAction = ((type === 'text' && inlineActionButton) ||
        (type === 'password' && showPassword)) &&
        !readOnly;
    const renderIcon = (error || (success && isSuccessSupported)) && !readOnly && !disabled;
    const surface = validateSurface(surfaceProp);
    const surfaceStyles = styles[surface.toLowerCase()];
    const componentContainerStyles = [styles.componentContainer, surfaceStyles];
    const colContainerStyles = [styles.col];
    const inputContainerStyles = [styles.inputContainer, surfaceStyles];
    const inputStyles = [styles.input, surfaceStyles];
    const creditCardIconContainerStyles = [styles.creditCardIconContainer];
    if (masked || type === 'securityCode') {
        inputStyles.push(styles.masked);
    }
    if (type === 'creditCard') {
        inputStyles.push(styles.creditCardMasked);
        if (!cardType) {
            creditCardIconContainerStyles.push(styles.placeholderIcon);
        }
        if (cardType === 'other') {
            creditCardIconContainerStyles.push(styles.genericIcon);
        }
    }
    const stickyPlaceholderStyles = [styles.stickyPlaceholder, surfaceStyles];
    if (isOpen) {
        inputContainerStyles.push(styles.openedCalendar);
    }
    if (focusedInput || isOpen) {
        inputContainerStyles.push(styles.focusedInput);
        if (keyboardFocused) {
            inputContainerStyles.push(styles.keyboardFocused);
        }
    }
    if (containerBackground === 'filled' ||
        (type === 'creditCard' && surface === 'darkBrandHighlight')) {
        inputContainerStyles.push(styles.filledBackground);
    }
    if (containerBackground === 'filled' &&
        readOnly &&
        surface.includes('dark')) {
        inputContainerStyles.push(styles.noBorder);
    }
    if (disabled) {
        inputContainerStyles.push(styles.disabled);
        stickyPlaceholderStyles.push(styles.disabled);
        componentContainerStyles.push(styles.disabled);
    }
    if (error) {
        inputContainerStyles.push(styles.error);
        stickyPlaceholderStyles.push(styles.error);
    }
    if (readOnly && !disabled) {
        inputContainerStyles.push(styles.readOnly);
    }
    if (success && !error && isSuccessSupported)
        inputContainerStyles.push(styles.success);
    if (containsPii)
        inputStyles.push('contains-PII');
    inputStyles.push(rest.className);
    if (helperTextPosition === 'right') {
        componentContainerStyles.push(styles.helperTextRight);
        colContainerStyles.push(styles.helperTextRight);
    }
    const _calculateMinWidth = () => {
        const minWidthMap = {
            password: 62,
            inlineActionButton: 102,
            creditCard: 288,
            tel: 176,
            securityCode: 88,
            date: 114,
            text: 40,
            calendar: undefined,
        };
        /* istanbul ignore else */
        if (minWidthMap[type])
            return calculateRem(minWidthMap[type]);
        calculateRem(40);
    };
    const _renderCreditCardIcon = (iconAriaLabel) => {
        const isDarkSurface = (() => {
            const isDarkBaseSurface = surface === 'darkPrimary' || surface === 'darkSecondary';
            const isDarkBrandHighlight = surface === 'darkBrandHighlight';
            // First case: dark primary/secondary with transparent background and not readonly
            if (isDarkBaseSurface && containerBackground !== 'filled' && !readOnly) {
                return true;
            }
            // Second case: dark brand highlight with readonly
            if (isDarkBrandHighlight && readOnly && !disabled) {
                return true;
            }
            return false;
        })();
        if (!cardType)
            return isDarkSurface ? (jsx(PlaceholderInverted, { ariaHidden: true, size: "100%" })) : (jsx(Placeholder, { ariaHidden: true, size: "100%" }));
        switch (cardType) {
            case 'visa':
                return isDarkSurface ? (jsx(VisaInverted, { ariaLabel: iconAriaLabel, size: "100%" })) : (jsx(Visa, { ariaLabel: iconAriaLabel, size: "100%" }));
            case 'amex':
                return jsx(Amex, { ariaLabel: iconAriaLabel, size: "100%" });
            case 'dinersClub':
                return jsx(Dinersclub, { ariaLabel: iconAriaLabel, size: "100%" });
            case 'discover':
                return jsx(Discover, { ariaLabel: iconAriaLabel, size: "100%" });
            case 'jcb':
                return jsx(Jcb, { ariaLabel: iconAriaLabel, size: "100%" });
            case 'mastercard':
                return (jsx("div", { className: styles.masterCardWrapper, children: jsx(Mastercard, { ariaLabel: iconAriaLabel }) }));
            case 'unionPay':
                return jsx(UnionPay, { ariaLabel: iconAriaLabel, size: "100%" });
            case 'other':
            default:
                return jsx(Placeholder, { ariaHidden: true, size: "100%" });
        }
    };
    const _dateAriaLabel = () => {
        switch (dateFormat) {
            case 'mmyy':
                return `${label} input, please enter two digits for the month and two digits for the year`;
            case 'mmddyy':
                return `${label} input, please enter two digits for the month, two digits for the day and two digits for the year`;
            case 'mmddyyyy':
                return `${label} input, please enter two digits for the month, two digits for the day and four digits for the year`;
            default:
                return undefined;
        }
    };
    const _determineAccessibilityLabel = () => {
        if (ariaLabel || rest['aria-label'])
            return ariaLabel || rest['aria-label'];
        if (type === 'date')
            return _dateAriaLabel();
        if (!required)
            return `${label || ''} Optional Input Field`;
        return label || '';
    };
    const _determineCalendarAriaLabel = (inputAriaLabel) => {
        const toExpandCalendar = isMobileDevice
            ? 'Double tap to expand calendar'
            : 'Press space/enter to expand calendar';
        if (isOpen)
            return `${inputAriaLabel} expanded`;
        if (valueProp)
            return `${inputAriaLabel} selected date ${valueProp} collapsed. ${toExpandCalendar}`;
        return `${inputAriaLabel} collapsed. ${toExpandCalendar}`;
    };
    const _determineAriaLabel = () => {
        const accessLabel = _determineAccessibilityLabel();
        const ariaLabelParts = [accessLabel];
        if (error && errorText) {
            ariaLabelParts.push(errorText);
        }
        else if (successText && success && isSuccessSupported) {
            ariaLabelParts.push(successText);
        }
        if (helperText) {
            ariaLabelParts.push(helperText);
        }
        let inputAriaLabel = ariaLabelParts.join(' ');
        /* istanbul ignore else */
        if (type === 'calendar') {
            inputAriaLabel = _determineCalendarAriaLabel(inputAriaLabel);
        }
        return inputAriaLabel;
    };
    const checkIfCompletedCardValue = () => value &&
        ((value.length === 16 && cardType === 'dinersClub') ||
            (value.length === 17 && cardType === 'amex') ||
            value.length === 19 ||
            (value.length === 20 && cardType === 'unionPay'));
    const checkEmptyError = () => {
        const isCompleteCardValue = checkIfCompletedCardValue();
        if (required &&
            (!value || (type === 'creditCard' && !isCompleteCardValue)) &&
            type !== 'calendar' &&
            !isOpen &&
            !value) {
            setError(true);
        }
        else {
            setError(false);
        }
    };
    const hasRendered = useRef(false);
    useEffect(() => {
        if (!hasRendered.current) {
            hasRendered.current = true; // Mark as rendered after the first render
        }
    }, [value]);
    useEffect(() => {
        if (cardBrand && type === 'securityCode') {
            const inputElement = document.querySelector(`input[id="${fieldId.current}"]`);
            if (inputElement) {
                inputElement.value = '';
            }
            setValue('');
            hasRendered.current = false;
        }
    }, [cardBrand, cardType]);
    const _onChange = (e) => {
        /* istanbul ignore else */
        if (!['creditCard', 'date'].includes(type) && onChange) {
            onChange(e);
        }
        if (required && e.target.value.length > 0)
            setEmptyError(false);
        let obj;
        switch (type) {
            case 'creditCard':
                e.target.value = _ignoreAlpha(e);
                obj = creditCardOnChange(e, value);
                /* istanbul ignore else */
                if (onChange) {
                    onChange(e, obj.cardType);
                }
                setValue(obj.value);
                setCardType(obj.cardType);
                return;
            case 'tel':
                obj = _formatToPhoneDash(e, value);
                setValue(obj.value);
                return;
            case 'date':
                e.target.value = _ignoreAlpha(e);
                obj = dateOnChange(e, value, dateFormat);
                setValue(obj.value);
                if (onChange)
                    onChange(e);
                return;
            case 'securityCode':
                e.target.value = _onlyNumbers(e);
            /* falls through */
            default:
                setValue(e.target.value);
        }
    };
    const _onPaste = (e) => {
        /* istanbul ignore else */
        if (type === 'tel') {
            const { currentTarget } = e;
            setTimeout(() => {
                currentTarget.setSelectionRange(currentTarget.value.length, currentTarget.value.length);
            });
        }
        /* istanbul ignore else */
        if (onPaste)
            onPaste(e);
    };
    const _determineValue = () => {
        /* istanbul ignore else */
        if (value || defaultValue) {
            /* istanbul ignore else */
            if (type === 'tel' || type === 'securityCode') {
                return value;
            }
            /* istanbul ignore else */
            if (type === 'creditCard') {
                return masked ? _maskValue(value) : value;
            }
            return value;
        }
        /* istanbul ignore else */
        if (type === 'creditCard' && readOnly) {
            return _maskValue(value);
        }
        /* istanbul ignore else */
        if (type === 'creditCard' && masked) {
            return _maskValue(value);
        }
        return value;
    };
    const _determineInputType = () => {
        /* istanbul ignore else */
        if (type === 'password') {
            /* istanbul ignore else */
            if (masked || readOnly) {
                return 'password';
            }
            return 'text';
        }
        /* istanbul ignore else */
        if (type === 'securityCode') {
            return 'password';
        }
        /* istanbul ignore else */
        if (type === 'date' || type === 'creditCard') {
            return 'text';
        }
        /* istanbul ignore else */
        if (type === 'calendar') {
            return undefined;
        }
        return type;
    };
    const _onFocus = (e) => {
        setFocusedInput(true);
        /* istanbul ignore else */
        if (readOnly) {
            return;
        }
        /* istanbul ignore else */
        if (onFocus) {
            onFocus(e);
        }
        const isCompleteCardValue = checkIfCompletedCardValue();
        /* istanbul ignore else */
        if (!readOnly) {
            /* istanbul ignore else */
            if (type === 'creditCard' && isCompleteCardValue) {
                setValue('');
                hasRendered.current = false;
                setCardType(undefined);
                setMasked(false);
            }
        }
    };
    const _onBlur = (e) => {
        /* istanbul ignore else */
        if (readOnly) {
            return;
        }
        /* istanbul ignore else */
        if (onBlur) {
            onBlur(e);
        }
        /* istanbul ignore else */
        if (required && !readOnly && !e.target.value) {
            setEmptyError(true);
        }
        const isCompleteCardValue = checkIfCompletedCardValue();
        /* istanbul ignore else */
        if (!readOnly) {
            /* istanbul ignore else */
            if (type === 'creditCard' && isCompleteCardValue) {
                setMasked(true);
            }
        }
        if (!errorProp)
            checkEmptyError();
        /* istanbul ignore else */
        if (type === 'creditCard') {
            e.target.setCustomValidity(!isCompleteCardValue ? ' ' : '');
        }
        /* istanbul ignore else */
        if (getOS() === 'ios' && getBrowser() === 'chrome') {
            // to prevent from onFocus triggering again after 'done' button pressed
            e.target.blur();
        }
        if (keyboardFocused)
            setKeyboardFocused(false);
        setFocusedInput(false);
    };
    const _onClickInlineAction = (e) => {
        /* istanbul ignore else */
        if (inlineActionButton === null || inlineActionButton === void 0 ? void 0 : inlineActionButton.onClick) {
            inlineActionButton === null || inlineActionButton === void 0 ? void 0 : inlineActionButton.onClick(e);
        }
        /* istanbul ignore else */
        if (type === 'password') {
            /* istanbul ignore else */
            if (inlineButtonWrapperRef.current) {
                inlineButtonWrapperRef.current.setAttribute('aria-live', 'assertive');
            }
            // Clear the previous timeout if it exists
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (masked) {
                setMasked(false);
                timeoutRef.current = setTimeout(() => {
                    if (inlineButtonWrapperRef.current) {
                        inlineButtonWrapperRef.current.setAttribute('aria-live', 'off');
                    }
                    setMasked(true);
                }, 10000);
            }
            else {
                setMasked(true);
            }
        }
    };
    const _onKeyDownInlineAction = (e) => {
        /* istanbul ignore else */
        if (e.key === 'Enter' || e.key === ' ') {
            /* istanbul ignore else */
            if (inlineActionButton === null || inlineActionButton === void 0 ? void 0 : inlineActionButton.onClick) {
                inlineActionButton === null || inlineActionButton === void 0 ? void 0 : inlineActionButton.onClick(e);
            }
            /* istanbul ignore else */
            if (type === 'password') {
                /* istanbul ignore else */
                if (inlineButtonWrapperRef.current) {
                    inlineButtonWrapperRef.current.setAttribute('aria-live', 'assertive');
                }
                // Clear the previous timeout if it exists
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                if (masked) {
                    setMasked(false);
                    timeoutRef.current = setTimeout(() => {
                        if (inlineButtonWrapperRef.current) {
                            inlineButtonWrapperRef.current.setAttribute('aria-live', 'off');
                        }
                        setMasked(true);
                    }, 10000);
                }
                else {
                    setMasked(true);
                }
            }
        }
    };
    const _renderStickyPlaceholder = () => (jsx("div", { className: stickyPlaceholderStyles.join(' '), children: stickyPlaceholderHTML(dateFormat, value) }));
    const _renderIcon = () => {
        const iconContainerStyles = [styles.iconContainer];
        if (error)
            iconContainerStyles.push(styles.errorIcon);
        if (success && isSuccessSupported)
            iconContainerStyles.push(styles.successIcon);
        return (jsx("div", { tabIndex: -1, "aria-hidden": "true", className: iconContainerStyles.join(' '), children: (() => {
                if (error) {
                    return jsx(Error, { size: 20 });
                }
                if (success && isSuccessSupported) {
                    return jsx(CheckmarkAlt, { size: 20 });
                }
            })() }));
    };
    const _renderInlineAction = () => {
        let inlineActionText;
        let inlineActionAriaLabel;
        let inlineActionProps;
        if (type === 'password') {
            if (masked) {
                inlineActionText = showPasswordButton === null || showPasswordButton === void 0 ? void 0 : showPasswordButton.label;
                inlineActionAriaLabel =
                    (showPasswordButton === null || showPasswordButton === void 0 ? void 0 : showPasswordButton.ariaLabel) || `${inlineActionText} password`;
                inlineActionProps = showPasswordButton;
            }
            else {
                inlineActionText = hidePasswordButton === null || hidePasswordButton === void 0 ? void 0 : hidePasswordButton.label;
                inlineActionAriaLabel =
                    (hidePasswordButton === null || hidePasswordButton === void 0 ? void 0 : hidePasswordButton.ariaLabel) || `${inlineActionText} password`;
                inlineActionProps = hidePasswordButton;
            }
        }
        else {
            inlineActionText = inlineActionButton === null || inlineActionButton === void 0 ? void 0 : inlineActionButton.label;
            inlineActionAriaLabel = (inlineActionButton === null || inlineActionButton === void 0 ? void 0 : inlineActionButton.ariaLabel)
                ? inlineActionButton.ariaLabel
                : inlineActionButton === null || inlineActionButton === void 0 ? void 0 : inlineActionButton.label;
            inlineActionProps = inlineActionButton;
        }
        return (jsx(TextLink, Object.assign({ role: "button", ariaLabel: inlineActionAriaLabel, disabled: disabled || readOnly, type: "standAlone", size: "large", onClick: _onClickInlineAction, onKeyDown: _onKeyDownInlineAction }, inlineActionProps, { surface: containerBackground === 'filled' ||
                (surface === 'darkBrandHighlight' && (error || success))
                ? 'lightPrimary'
                : surface, children: inlineActionText })));
    };
    const _determineInputMode = () => {
        switch (type) {
            case 'creditCard':
            case 'tel':
            case 'securityCode':
            case 'number':
            case 'date':
                return 'numeric'; // Ensures numeric keyboard
            default:
                return undefined; // Default input mode
        }
    };
    const handleContainerMouseDown = (event) => {
        // Prevent the mousedown event from causing the input to lose focus
        if (event.target === event.currentTarget) {
            event.preventDefault();
        }
    };
    const handleContainerClick = (event) => {
        if (event.target !== event.currentTarget) {
            return;
        }
        const inputElement = event.currentTarget.querySelector('input');
        if (inputElement && document.activeElement !== inputElement) {
            inputElement.focus(); // Only focus if the input is not already focused
        }
    };
    const _renderHelperText = () => (jsx(HelperText, { helperText: helperText, helperTextPosition: helperTextPosition, disabled: disabled, surface: surface }));
    const _renderSecurityCodeTooltip = () => {
        const regularDescription = `Most credit or debit cards have a 3-digit security code on the
    back.`;
        const AMEXDescription = `American Express cards have a 4-digit code on the front.`;
        const securityCodeTooltipWrapperStyles = [
            styles.securityCodeTooltipWrapper,
            styles[surface],
        ];
        if (tooltip === null || tooltip === void 0 ? void 0 : tooltip.colorMode) {
            securityCodeTooltipWrapperStyles.push(styles[tooltip === null || tooltip === void 0 ? void 0 : tooltip.colorMode]);
        }
        const securityCodeTooltipIconWrapperStyles = [
            styles.securityCodeTooltipIconWrapper,
            styles[surface],
        ];
        const DescriptionStyles = [styles.description, styles[surface]];
        const columnWrapperStyles = [styles.columnWrapper];
        if (isMobileDevice) {
            securityCodeTooltipWrapperStyles.push(styles.mobile);
            securityCodeTooltipIconWrapperStyles.push(styles.mobile);
            DescriptionStyles.push(styles.mobile);
        }
        const _renderAmexIcon = () => jsx(SecurityCodeAmex, { size: "100%", ariaHidden: true });
        const _renderDefaultIcon = () => jsx(SecurityCode, { size: "100%", ariaHidden: true });
        if (cardBrand !== 'other' && cardBrand !== undefined) {
            securityCodeTooltipWrapperStyles.push(styles.hasCard);
            return (jsxs("span", { className: securityCodeTooltipWrapperStyles.join(' '), children: [jsx("span", { className: securityCodeTooltipIconWrapperStyles.join(' '), children: cardBrand === 'amex' ? _renderAmexIcon() : _renderDefaultIcon() }), jsx("span", { className: DescriptionStyles.join(' '), children: cardBrand === 'amex' ? AMEXDescription : regularDescription })] }));
        }
        return (jsxs("span", { className: securityCodeTooltipWrapperStyles.join(' '), children: [jsxs("span", { className: columnWrapperStyles.join(' '), children: [jsx("span", { className: securityCodeTooltipIconWrapperStyles.join(' '), children: _renderDefaultIcon() }), jsx("span", { className: DescriptionStyles.join(' '), children: isMobileDevice ? (regularDescription) : (jsxs(Fragment, { children: [regularDescription, "\u00A0"] })) })] }), jsxs("span", { className: columnWrapperStyles.join(' '), children: [jsx("span", { className: securityCodeTooltipIconWrapperStyles.join(' '), children: _renderAmexIcon() }), jsx("span", { className: DescriptionStyles.join(' '), children: AMEXDescription })] })] }));
    };
    const labelTooltip = type === 'securityCode'
        ? Object.assign(Object.assign({}, (tooltip || {})), { size: 'small', disabled,
            surface, ariaLabel: 'security code tooltip', closeButtonText: 'Close', title: null, children: _renderSecurityCodeTooltip() }) : Object.assign({}, (tooltip && tooltip));
    const creditCardIconAriaLabel = cardType && cardType !== 'other' ? `${cardType} card` : '';
    return (jsx("div", { id: componentContainerId !== null && componentContainerId !== void 0 ? componentContainerId : undefined, className: componentContainerStyles.join(' '), style: {
            '--input-width': width,
            '--input-min-width': _calculateMinWidth(),
        }, children: jsxs("div", { className: styles.innerContainer, children: [label && (jsx(Label, { disabled: disabled, required: required, label: label, tooltip: labelTooltip, type: type, "data-testid": `${testId}-label`, id: labelId, "aria-label": label, htmlFor: (_b = fieldId.current) !== null && _b !== void 0 ? _b : undefined, helperTextPosition: helperTextPosition, helperText: helperText, surface: surface, onClick: (e) => {
                        if (isCalendar) {
                            e.preventDefault();
                        }
                    } })), jsxs("div", { className: styles.row, children: [jsxs("div", { className: colContainerStyles.join(' '), "aria-live": (isCalendar && isMobileDevice) || !isAndroidDevice
                                ? undefined
                                : 'assertive', children: [jsxs("div", { className: inputContainerStyles.join(' '), onClick: handleContainerClick, onMouseDown: handleContainerMouseDown, children: [renderInputIcon && renderInputIcon(), type === 'creditCard' && (jsx("div", { className: creditCardIconContainerStyles.join(' '), children: _renderCreditCardIcon(creditCardIconAriaLabel) })), jsx(ScreenReaderText, { "aria-live": "polite", "aria-atomic": "true", children: showCardTypeAlert &&
                                                (cardType === 'other'
                                                    ? 'card type not detected'
                                                    : `${cardType} card type detected`) }), jsxs("div", { className: styles.controlWrapper, "aria-live": isCalendar && isOpen ? 'assertive' : 'off', onClick: isCalendar && isAndroidDevice && onClick ? onClick : undefined, tabIndex: isCalendar && isAndroidDevice ? -1 : undefined, "aria-label": isCalendar && isAndroidDevice
                                                ? _determineAriaLabel()
                                                : undefined, children: [jsx("input", Object.assign({ ref: inputRef, style: { caretColor }, "data-testid": testId, maxLength: _calculateMaxLength(cardType, type, dateFormat, cardBrand, maxLength), "aria-label": _determineAriaLabel(), id: (_c = fieldId.current) !== null && _c !== void 0 ? _c : undefined, autoComplete: autoComplete, inputMode: _determineInputMode() }, rest, { className: inputStyles.join(' ').trim(), placeholder: undefined, onClick: type === 'calendar' && isAndroidDevice ? undefined : onClick, disabled: disabled, required: required, "aria-invalid": false, readOnly: readOnly, onChange: _onChange, onPaste: _onPaste, value: type === 'password' || type === 'securityCode'
                                                        ? valueProp
                                                        : _determineValue(), type: _determineInputType(), "aria-hidden": type === 'calendar' && isAndroidDevice, onFocus: _onFocus, onBlur: _onBlur })), type === 'date' && _renderStickyPlaceholder()] }), (renderIcon || renderInlineAction) && (jsxs("div", Object.assign({ ref: inlineButtonWrapperRef, className: styles.actionWrapper, id: `inlineButtonWrapper-${inputId}` }, (renderInlineAction && type === 'password'
                                            ? { 'aria-live': 'off', 'aria-relevant': 'additions' }
                                            : {}), { children: [!focusedInput &&
                                                    renderIcon &&
                                                    !(isCalendar && isOpen) &&
                                                    _renderIcon(), renderInlineAction && _renderInlineAction()] })))] }), ((error && errorText) || (success && successText)) &&
                                    !disabled &&
                                    !readOnly && (jsx("div", { role: !(getOS() === 'android')
                                        ? /* istanbul ignore next */ 'alert'
                                        : undefined, children: jsx(StatusText, { errorText: errorText, error: error, success: success, successText: successText, surface: surface }) }))] }), helperText && helperTextPosition === 'right' && (jsx("div", { className: styles.helperTextRightWrapper, children: _renderHelperText() }))] }), helperText && helperTextPosition === 'bottom' && _renderHelperText()] }) }));
};

export { InputField as default };
